import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { STATE_STYLE, STATE_ORDER } from "@/components/StateBadge";
import { Package, TrendingUp, CheckCircle, AlertCircle, Plus, Upload, Package2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function ClienteDashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/cliente").then((r) => setData(r.data));
    api.get("/orders?limit=8").then((r) => setRecent(r.data));
  }, []);

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Portal aliado</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Tus pedidos</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/cliente/nuevo" data-testid="btn-nuevo-pedido" className="bg-yellow-300 text-black font-bold px-5 py-3 uppercase tracking-widest text-sm hover:bg-yellow-200 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo pedido
          </Link>
          <Link to="/cliente/masivo" data-testid="btn-carga-masiva" className="border border-white/20 hover:border-yellow-300 font-bold px-5 py-3 uppercase tracking-widest text-sm inline-flex items-center gap-2">
            <Upload className="w-4 h-4" /> Carga Excel
          </Link>
          <Link to="/cliente/bultos" data-testid="btn-bultos" className="border border-white/20 hover:border-yellow-300 font-bold px-5 py-3 uppercase tracking-widest text-sm inline-flex items-center gap-2">
            <Package2 className="w-4 h-4" /> Bultos
          </Link>
        </div>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-5 h-5 text-yellow-300" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">Hoy</span>
              </div>
              <div className="font-display font-black text-5xl tracking-tighter">{data.hoy_creados}</div>
              <div className="font-mono-tech text-xs text-zinc-500 mt-2">Nuevos pedidos</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-5 h-5 text-yellow-300" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">En reparto</span>
              </div>
              <div className="font-display font-black text-5xl tracking-tighter">{data.state_counts.en_reparto}</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-300" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">Entregados</span>
              </div>
              <div className="font-display font-black text-5xl tracking-tighter">{data.state_counts.entregado}</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <AlertCircle className="w-5 h-5 text-red-300" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">Incidencias</span>
              </div>
              <div className="font-display font-black text-5xl tracking-tighter">{data.state_counts.no_entregado}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-7 gap-px bg-white/10 mb-8">
            {STATE_ORDER.map((s) => (
              <div key={s} className="bg-zinc-950 p-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{STATE_STYLE[s]?.label}</div>
                <div className="font-display font-black text-2xl">{data.state_counts[s] ?? 0}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">/ Últimos pedidos</div>
          <Link to="/cliente/pedidos" className="text-xs font-mono-tech uppercase tracking-widest text-yellow-300">Ver todos →</Link>
        </div>
        <table className="w-full">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">Tracking</th>
              <th className="px-4 py-3">Destinatario</th>
              <th className="px-4 py-3">Comuna</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Fecha</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {recent.map((o) => (
              <tr key={o.id} className="hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono-tech text-sm">{o.tracking_number}</td>
                <td className="px-4 py-3 text-sm">{o.destinatario?.nombre}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{o.destinatario?.comuna}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono-tech uppercase tracking-wider ${STATE_STYLE[o.state]?.color}`}>
                    <span className="w-1 h-1 rounded-full bg-current" />
                    {STATE_STYLE[o.state]?.label}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-tech text-xs text-zinc-400">{new Date(o.created_at).toLocaleDateString("es-CL")}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500 font-mono-tech text-sm">Aún no tienes pedidos. Crea el primero →</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
